/**
 * Design Tokens for Watchlist App
 * Centralized design system with shared and theme-specific colors
 */

import { SxProps } from '@mui/material';
import { getFontFamily } from '../config/fonts';

// ============================================================================
// BASE COLORS (SHARED BETWEEN THEMES)
// ============================================================================

const baseColors = {
  // Brand Colors (same in both themes)
  // 🎨 UPDATE THESE VALUES TO CHANGE YOUR BRAND COLORS:
  brand: {
    light: { 
      primary: '#D30F45',    // 🔹 Change this for light theme primary brand color
      primaryHover: '#B30D3B', // 🔹 Change this for light theme primary hover state (20% darker)
      secondary: '#3634A3',   // 🔹 Change this for light theme secondary brand color
      secondaryHover: '#2D2B89' // 🔹 Change this for light theme secondary hover state (20% darker)
    },
    dark: { 
      primary: '#D30F45',    // 🔹 Change this for dark theme primary brand color
      primaryHover: '#FF1252', // 🔹 Change this for dark theme primary hover state (20% lighter)
      secondary: '#7D7AFF',   // 🔹 Change this for dark theme secondary brand color
      secondaryHover: '#9794FF' // 🔹 Change this for dark theme secondary hover state (20% lighter)
    },
  },
  
  // Semantic Colors
  // 🎨 UPDATE THESE VALUES TO CHANGE SEMANTIC COLORS:
  semantic: {
    light: { 
      success: '#30DB5B',     // 🟢 Success/positive action color
      successHover: '#28C248', // 🟢 Success hover state
      error: '#FF453A',       // 🔴 Error/danger color
      warning: '#FF9F0A',     // 🟡 Warning color
      info: '#0A84FF'         // 🔵 Info/neutral action color
    },
    dark: { 
      success: '#30DB5B',     // 🟢 Success/positive action color (dark theme)
      successHover: '#28C248', // 🟢 Success hover state (dark theme)
      error: '#FF453A',       // 🔴 Error/danger color (dark theme)
      warning: '#FF9F0A',     // 🟡 Warning color (dark theme)
      info: '#0A84FF'         // 🔵 Info/neutral action color (dark theme)
    },
  },
  
  // Rating Colors (identical in both themes)
  rating: { star: '#FFD700', rottenTomatoes: '#F93208', imdb: '#F6C700' },
  
  // Background Colors
  // 🎨 UPDATE THESE VALUES TO CHANGE BACKGROUND COLORS:
  background: {
    light: { 
      primary: '#FFFFFF',     // ⬜ Main background color (light theme)
      secondary: '#F8F8F8',   // ⬜ Secondary background color (light theme)
      elevated: '#FFFFFF',    // ⬜ Elevated surface color (light theme)
      dark: '#2C2C2E',        // ⬛ Dark background option (light theme)
      overlay: 'rgba(255, 255, 255, 0.9)',    // 🌫️ Overlay/modal background (light theme)
      glass: 'rgba(112, 0, 240, 0.11)', // 🪟 Glass background for dialogs (light theme)
      glassBorder: 'rgba(255, 255, 255, 0.2)', // 🪟 Glass border (light theme)
    },
    dark: { 
      primary: '#2C2C2E',     // ⬛ Main background color (dark theme)
      secondary: '#1C1C1E',   // ⬛ Secondary background color (dark theme)
      elevated: '#2C2C2E',    // ⬛ Elevated surface color (dark theme)
      dark: 'rgba(0, 0, 0, 0.05)',        // ⬛ Dark background option (dark theme)
      overlay: 'rgba(0, 0, 0, 0.9)',    // 🌫️ Overlay/modal background (dark theme)
      glass: 'rgba(34, 34, 42, 0.4)', // 🪟 Glass background for dialogs (dark theme)
      glassBorder: 'rgba(255, 255, 255, 0.2)', // 🪟 Glass border (dark theme)
    },
  },
  
  // Text Colors
  text: {
    light: { primary: 'rgba(255, 255, 255, 0.85)', secondary: 'rgba(255, 255, 255, 0.6)', tertiary: 'rgba(255, 255, 255, 0.4)', inverse: '#FFFFFF', inverseSecondary: '#FFFFFFB3' },
    dark: { primary: 'rgba(255, 255, 255, 0.85)', secondary: 'rgba(255, 255, 255, 0.6)', tertiary: 'rgba(255, 255, 255, 0.4)', inverse: '#000000', inverseSecondary: '#000000B3' },
  },
  
  // UI Colors
  ui: {
    light: { border: 'rgba(255, 255, 255, 0.18)', borderLight: 'rgba(255, 255, 255, 0.12)', separator: 'rgba(255, 255, 255, 0.12)', separatorLight: 'rgba(255, 255, 255, 0.08)', hover: 'rgba(255, 255, 255, 0.05)', active: 'rgba(255, 255, 255, 0.12)', disabled: 'rgba(255, 255, 255, 0.18)' },
    dark: { border: 'rgba(255, 255, 255, 0.18)', borderLight: 'rgba(255, 255, 255, 0.12)', separator: 'rgba(255, 255, 255, 0.12)', separatorLight: 'rgba(255, 255, 255, 0.08)', hover: 'rgba(255, 255, 255, 0.05)', active: 'rgba(255, 255, 255, 0.12)', disabled: 'rgba(255, 255, 255, 0.18)' },
  },
  
  // Default Watchlist Card Colors (same in both themes - Figma specific)
  defaultWatchlistCard: { title: 'rgba(255, 255, 255, 0.9)', subtitle: 'rgba(255, 255, 255, 0.6)', icon: 'rgba(255, 255, 255, 0.4)' },
  
  // Watchlist Card Colors (theme-specific - for community/other cards)
  watchlistCard: {
    light: { 
      title: '#DFD0D0',    // Light grayish for light theme
      subtitle: '#C0B0B0',  // Slightly darker gray for subtitle
      icon: '#B0A0A0',     // Even darker gray for icon
    },
    dark: { 
      title: '#FFFFFF',     // Pure white for title (from Figma)
      subtitle: '#FFFFFF99', // 60% white opacity for subtitle (from Figma)
      icon: '#EBEBF54D',    // 30% light gray opacity for icon (from Figma)
    },
  },
};

// ============================================================================
// COLOR TOKENS INTERFACE
// ============================================================================

export interface ColorTokens {
  background: { primary: string; secondary: string; elevated: string; dark: string; overlay: string; glass: string; glassBorder: string };
  text: { primary: string; secondary: string; tertiary: string; inverse: string; inverseSecondary: string };
  defaultWatchlistCard: { title: string; subtitle: string; icon: string };
  watchlistCard: { title: string; subtitle: string; icon: string };
  brand: { primary: string; primaryHover: string; secondary: string };
  semantic: { success: string; successHover: string; error: string; warning: string; info: string };
  ui: { border: string; borderLight: string; separator: string; separatorLight: string; hover: string; active: string; disabled: string };
  rating: { star: string; rottenTomatoes: string; imdb: string };
}

// ============================================================================
// THEME COLOR EXPORTS
// ============================================================================

export const lightModeColors: ColorTokens = {
  background: baseColors.background.light,
  text: baseColors.text.light,
  brand: baseColors.brand.light,
  semantic: baseColors.semantic.light,
  ui: baseColors.ui.light,
  defaultWatchlistCard: baseColors.defaultWatchlistCard,
  watchlistCard: baseColors.watchlistCard.light,
  rating: baseColors.rating,
};

export const darkModeColors: ColorTokens = {
  background: baseColors.background.dark,
  text: baseColors.text.dark,
  brand: baseColors.brand.dark,
  semantic: baseColors.semantic.dark,
  ui: baseColors.ui.dark,
  defaultWatchlistCard: baseColors.defaultWatchlistCard,
  watchlistCard: baseColors.watchlistCard.dark,
  rating: baseColors.rating,
};

// ============================================================================
// TYPOGRAPHY TOKENS
// ============================================================================

export interface TypographyTokens {
  fontFamily: { primary: string; secondary: string };
  fontSize: { xs: { mobile: string; tablet: string; desktop: string }; sm: { mobile: string; tablet: string; desktop: string }; base: { mobile: string; tablet: string; desktop: string }; lg: { mobile: string; tablet: string; desktop: string }; xl: { mobile: string; tablet: string; desktop: string }; '2xl': { mobile: string; tablet: string; desktop: string }; '3xl': { mobile: string; tablet: string; desktop: string } };
  fontWeight: { normal: number; medium: number; semibold: number; bold: number };
  lineHeight: { tight: number; normal: number; relaxed: number; loose: number };
  letterSpacing: { tight: string; normal: string; wide: string };
}

export const typography: TypographyTokens = {
  fontFamily: {
    primary: getFontFamily(),
    secondary: getFontFamily(),
  },
  fontSize: {
    xs: { mobile: '10px', tablet: '11px', desktop: '12px' },
    sm: { mobile: '12px', tablet: '13px', desktop: '14px' },
    base: { mobile: '14px', tablet: '15px', desktop: '16px' },
    lg: { mobile: '16px', tablet: '17px', desktop: '18px' },
    xl: { mobile: '18px', tablet: '20px', desktop: '20px' },
    '2xl': { mobile: '20px', tablet: '24px', desktop: '28px' },
    '3xl': { mobile: '24px', tablet: '28px', desktop: '32px' },
  },
  fontWeight: { normal: 400, medium: 500, semibold: 600, bold: 700 },
  lineHeight: { tight: 1.2, normal: 1.33, relaxed: 1.5, loose: 1.57 },
  letterSpacing: { tight: '-0.024em', normal: '0em', wide: '0.025em' },
};

// ============================================================================
// SPACING & RADIUS TOKENS
// ============================================================================

export const spacing = {
  xs: { mobile: 0.5, tablet: 1, desktop: 1 },
  sm: { mobile: 1, tablet: 1.5, desktop: 2 },
  base: { mobile: 1.5, tablet: 2, desktop: 2.5 },
  lg: { mobile: 2, tablet: 3, desktop: 4 },
  xl: { mobile: 3, tablet: 4, desktop: 5 },
  '2xl': { mobile: 4, tablet: 5, desktop: 6 },
  '3xl': { mobile: 6, tablet: 8, desktop: 10 },
};

export const borderRadius = {
  xs: { mobile: 0.5, tablet: 1, desktop: 1 },
  sm: { mobile: 1, tablet: 1.5, desktop: 2 },
  base: { mobile: 2, tablet: 2.5, desktop: 3 },
  md: { mobile: 3, tablet: 4, desktop: 5 },
  lg: { mobile: 12, tablet: 16, desktop: 17 },
  xl: { mobile: 16, tablet: 20, desktop: 24 },
};

export const borderRadiusSpecial = { full: '50%' };

// ============================================================================
// POSTER COLLAGE TOKENS
// ============================================================================

export const posterCollage = {
  borderRadius: {
    small: '4px',  // For small collages (grid view)
    medium: '4px', // For medium collages (list view)
    large: '4px',  // For large collages
  },
  placeholder: {
    light: 'rgba(0, 0, 0, 0.03)',      // Subtle placeholder in light mode
    dark: 'rgba(255, 255, 255, 0.02)', // Subtle placeholder in dark mode
  },
  shimmer: {
    light: {
      background: 'linear-gradient(90deg, rgba(0, 0, 0, 0.015) 0%, rgba(0, 0, 0, 0.04) 50%, rgba(0, 0, 0, 0.015) 100%)',
      overlay: 'linear-gradient(90deg, rgba(255,255,255,0.0) 0%, rgba(255,255,255,0.3) 40%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.3) 60%, rgba(255,255,255,0.0) 100%)',
    },
    dark: {
      background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.01) 0%, rgba(255, 255, 255, 0.03) 50%, rgba(255, 255, 255, 0.01) 100%)',
      overlay: 'linear-gradient(90deg, rgba(255,255,255,0.0) 0%, rgba(255,255,255,0.1) 40%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.1) 60%, rgba(255,255,255,0.0) 100%)',
    },
  },
  sizes: {
    grid: { width: 36, height: 68 },    // Small grid collages
    list: { width: 114, height: 68 },   // List view collages
    card: { width: 179, height: 111 },  // Large card collages
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const getColors = (isDarkMode: boolean): ColorTokens => isDarkMode ? darkModeColors : lightModeColors;

export const createTypographyStyle = (
  size: keyof TypographyTokens['fontSize'],
  weight: keyof TypographyTokens['fontWeight'] = 'normal',
  lineHeight: keyof TypographyTokens['lineHeight'] = 'normal'
): SxProps => ({
  fontFamily: typography.fontFamily.primary,
  fontSize: { xs: typography.fontSize[size].mobile, sm: typography.fontSize[size].tablet, md: typography.fontSize[size].desktop },
  fontWeight: typography.fontWeight[weight],
  lineHeight: typography.lineHeight[lineHeight],
  letterSpacing: typography.letterSpacing.normal,
});

export const createSpacing = (size: keyof typeof spacing) => ({ xs: spacing[size].mobile, sm: spacing[size].tablet, md: spacing[size].desktop });

export const createBorderRadius = (size: keyof typeof borderRadius) => ({ xs: borderRadius[size].mobile, sm: borderRadius[size].tablet, md: borderRadius[size].desktop });

// ============================================================================
// GLASS MORPHISM STYLES (HEAVILY USED)
// ============================================================================

export const glassStyles = {
  dialog: (isDarkMode: boolean) => {
    const colors = getColors(isDarkMode);
    return {
      bgcolor: baseColors.background[isDarkMode ? 'dark' : 'light'].glass,
      backdropFilter: 'blur(25px)',
      WebkitBackdropFilter: 'blur(25px)', // Safari support
      border: `1px solid ${colors.background.glassBorder}`,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
      borderRadius: createBorderRadius('md'),
    };
  },
  bottomSheet: (isDarkMode: boolean) => {
    const colors = getColors(isDarkMode);
    return {
      bgcolor: baseColors.background[isDarkMode ? 'dark' : 'light'].glass,
      backdropFilter: 'blur(25px)',
      WebkitBackdropFilter: 'blur(25px)', // Safari support
      border: `1px solid ${colors.background.glassBorder}`,
      borderBottom: 'none',
      boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.4)',
      borderRadius: '24px 24px 0 0',
    };
  },
  popup: (isDarkMode: boolean) => {
    const colors = getColors(isDarkMode);
    return {
      bgcolor: baseColors.background[isDarkMode ? 'dark' : 'light'].glass,
      backdropFilter: 'blur(25px)',
      WebkitBackdropFilter: 'blur(25px)', // Safari support
      border: `1px solid ${colors.background.glassBorder}`,
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.6)',
      borderRadius: createBorderRadius('md'),
    };
  },
  menu: (isDarkMode: boolean) => {
    const colors = getColors(isDarkMode);
    return {
      bgcolor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)', // Safari support
      border: `1px solid ${colors.background.glassBorder}`,
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
      borderRadius: 3,
    };
  },
};

// Note: textStyles removed - use createTypographyStyle() directly or prefer 
// the newer componentTokens.ts system for consistent styling

// ============================================================================
// DESIGN TOKENS EXPORTS
// ============================================================================
// All exports are available as named exports for tree-shaking efficiency 